"use client";
import { supabase } from 'src/configs/supabaseClient';
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useAuth } from 'src/hooks/useAuth';
import { useGetReminderSettingsByUserId } from 'src/hooks/reminder-settings/useGetReminderSettingsByUserId';
import { companyDocumentConfigs, getCompanyDocumentFieldName } from 'src/constants/companyDocuments';

interface ReminderExpirationsData {
  company_id: string;
  vehicle_id?: string | null;
  driver_id?: string | null;
  file_type: string;
  expiration_date: any;
  isEmailSent: boolean;
  file_name: string;
  company_name: string;
  sent_to_email_address: string;
  vehicle_document_expirations_id?: string | null;
  driver_document_expirations_id?: string | null;
  driver_name?: string;
  document_scope?: 'vehicle' | 'driver' | 'company';
  company_document_key?: string;
  isExpirationDateUpdated?: boolean;
}

export const useGetAllReminders = (): UseQueryResult<ReminderExpirationsData[], Error> => {
  const auth = useAuth();

  const {
    data: reminderSettingsData,
    isLoading: isReminderSettingsLoading,
    isError: isReminderSettingsError
  } = useGetReminderSettingsByUserId(auth?.user?.id || '');

  const calculateExpiryThreshold = (reminderValue: any, reminderUnit: any) => {
    const currentDate = new Date();
    if (reminderUnit === 'months') {
      currentDate.setMonth(currentDate.getMonth() + parseInt(reminderValue));
    } else if (reminderUnit === 'weeks') {
      currentDate.setDate(currentDate.getDate() + 7 * parseInt(reminderValue));
    }

    return currentDate;
  };

  return useQuery({
    queryKey: ['reminders'],
    queryFn: async () => {
      const expiryThreshold = calculateExpiryThreshold(reminderSettingsData?.reminder_value, reminderSettingsData?.reminder_unit);
      const formattedExpiryThreshold = expiryThreshold.toISOString().split('T')[0];

      const { data: vehicleData, error: vehicleError } = await supabase
        .from('vehicle_document_expirations')
        .select('*')
        .lte('expiration_date', formattedExpiryThreshold)



      const { data: driverData, error: driverError } = await supabase
        .from('driver_document_expirations')
        .select('*')
        .lte('expiration_date', formattedExpiryThreshold)



      const companyDateFields = companyDocumentConfigs.flatMap(doc => [
        getCompanyDocumentFieldName(doc.key, 'from'),
        getCompanyDocumentFieldName(doc.key, 'to')
      ])

      const companySelectFields = ['company_id', 'company_name', 'company_email_address', ...companyDateFields].join(', ')

      type CompanyReminderRow = {
        company_id: string
        company_name: string
        company_email_address: string | null
        [key: string]: any
      }

      const { data: companyDocs, error: companyDocsError } = await supabase
        .from('companies')
        .select(companySelectFields);

      if (vehicleError || driverError || companyDocsError) {
        console.error('Error fetching reminders:', vehicleError, driverError, companyDocsError);
        throw new Error(vehicleError?.message || driverError?.message || companyDocsError?.message);
      }

      // Fetch driver names for driver documents
      const driverIds = driverData?.map(d => d.driver_id).filter(Boolean) || [];
      let driverNames: Record<string, string> = {};
      
      if (driverIds.length > 0) {
        const { data: drivers, error: driversError } = await supabase
          .from('drivers')
          .select('*')
          .in('driver_id', driverIds);
        
        if (!driversError && drivers && drivers.length > 0) {
          driverNames = drivers.reduce((acc, driver) => {
            if (driver.driver_id && driver.name) {
              acc[driver.driver_id] = driver.name;
            }
            return acc;
          }, {} as Record<string, string>);
        }
      }

      // Transform driver data to include driver_name
      const transformedDriverData = driverData?.map(item => ({
        ...item,
        driver_name: driverNames[item.driver_id] || ''
      })) || [];

      const todayIso = new Date().toISOString().split('T')[0];

      const typedCompanyDocs = ((companyDocs || []) as unknown as CompanyReminderRow[])

      const companyReminders =
        typedCompanyDocs.flatMap(company => {
          return companyDocumentConfigs.reduce((acc, doc) => {
            const toField = getCompanyDocumentFieldName(doc.key, 'to')
            const expirationDate = company[toField as keyof typeof company] as string | null

            if (
              !expirationDate ||
              expirationDate > formattedExpiryThreshold ||
              expirationDate <= todayIso
            ) {
              return acc
            }

            acc.push({
              company_id: company.company_id,
              file_type: doc.label,
              expiration_date: expirationDate,
              isEmailSent: false,
              file_name: `${doc.label} Compliance`,
              company_name: company.company_name,
              sent_to_email_address: company.company_email_address,
              vehicle_document_expirations_id: null,
              driver_document_expirations_id: null,
              document_scope: 'company',
              company_document_key: `${company.company_id}-${doc.key}`,
              isExpirationDateUpdated: false
            } as ReminderExpirationsData)

            return acc
          }, [] as ReminderExpirationsData[])
        }) || []

      return [...vehicleData, ...transformedDriverData, ...companyReminders];
    },
    enabled: !!auth?.user && !isReminderSettingsLoading
  });
};
